import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import { cn } from "../lib/cn.js";

const bannerStyles = cva(
  "flex items-start gap-3 rounded-md border px-3 py-2 text-sm",
  {
    variants: {
      variant: {
        info: "bg-accent-soft/40 text-fg border-accent/30",
        success: "bg-success/10 text-fg border-success/30",
        warning: "bg-warning/10 text-fg border-warning/40",
        danger: "bg-danger/10 text-fg border-danger/40",
      },
    },
    defaultVariants: { variant: "info" },
  },
);

export type BannerProps = HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof bannerStyles> & {
    readonly icon?: ReactNode;
    readonly title?: ReactNode;
    readonly children?: ReactNode;
  };

export const Banner = forwardRef<HTMLDivElement, BannerProps>(function Banner(
  { className, variant, icon, title, children, role, ...rest },
  ref,
) {
  const computedRole =
    role ?? (variant === "danger" || variant === "warning" ? "alert" : "status");
  return (
    <div
      ref={ref}
      role={computedRole}
      className={cn(bannerStyles({ variant }), className)}
      {...rest}
    >
      {icon !== undefined ? (
        <span className="flex-none mt-0.5" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <div className="flex-1 min-w-0">
        {title !== undefined ? (
          <div className="font-semibold mb-0.5">{title}</div>
        ) : null}
        {children !== undefined ? <div className="text-fg">{children}</div> : null}
      </div>
    </div>
  );
});
